import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, Settings as SettingsIcon, CalendarOff, Clock, Plus, Trash2, Save, Lock, Zap, Pencil, BookOpen } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Quick setup schedule - Mon/Wed for all classes
const QUICK_SETUP_SCHEDULE = [
  { class_type: "Tigers Kung Fu", day_of_week: "monday" },
  { class_type: "Tigers Kung Fu", day_of_week: "wednesday" },
  { class_type: "Dragons Kung Fu", day_of_week: "monday" },
  { class_type: "Dragons Kung Fu", day_of_week: "wednesday" },
  { class_type: "Adults Kung Fu", day_of_week: "monday" },
  { class_type: "Adults Kung Fu", day_of_week: "wednesday" },
];

const DAYS_OF_WEEK = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const CLASS_TYPES = ["Tigers Kung Fu", "Dragons Kung Fu", "Adults Kung Fu"];

export default function Settings() {
  const [activeTab, setActiveTab] = useState("schedule");
  const [loading, setLoading] = useState(true);
  
  // Class Schedule State
  const [classSchedule, setClassSchedule] = useState([]);
  const [newSchedule, setNewSchedule] = useState({
    class_type: "",
    day_of_week: "",
    start_time: "18:00"
  });
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  
  // Holidays State
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({
    start_date: "",
    end_date: "",
    reason: "",
    affects_class: "all"
  });
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  
  // PIN State
  const [oldPin, setOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [changingPin, setChangingPin] = useState(false);

  // v7.4: Class Types Management State
  const [classTypes, setClassTypes] = useState([]);
  const [newClassName, setNewClassName] = useState("");
  const [newClassColor, setNewClassColor] = useState("gray");
  const [editingClassType, setEditingClassType] = useState(null);
  const [editClassName, setEditClassName] = useState("");
  const [editClassColor, setEditClassColor] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scheduleRes, holidaysRes, classTypesRes] = await Promise.all([
        axios.get(`${API}/class-schedule`),
        axios.get(`${API}/school-closures`),
        axios.get(`${API}/class-types`),
      ]);
      setClassSchedule(scheduleRes.data);
      setHolidays(holidaysRes.data);
      setClassTypes(classTypesRes.data);
    } catch (error) {
      console.log("Settings data not loaded:", error);
    } finally {
      setLoading(false);
    }
  };

  // Class Schedule Functions
  const handleAddSchedule = async () => {
    if (!newSchedule.class_type || !newSchedule.day_of_week) {
      toast.error("Please select class type and day");
      return;
    }
    
    try {
      await axios.post(`${API}/class-schedule`, newSchedule);
      toast.success("Class schedule added");
      setScheduleDialogOpen(false);
      setNewSchedule({ class_type: "", day_of_week: "", start_time: "18:00" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add schedule");
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!confirm("Delete this class from the schedule?")) return;
    try {
      await axios.delete(`${API}/class-schedule/${id}`);
      toast.success("Schedule removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete schedule");
    }
  };

  // Quick Setup - Add Mon/Wed for all classes
  const handleQuickSetup = async () => {
    if (!confirm("This will add Monday & Wednesday schedules for Tigers, Dragons, and Adults. Continue?")) return;
    
    let added = 0;
    let skipped = 0;
    
    for (const schedule of QUICK_SETUP_SCHEDULE) {
      try {
        await axios.post(`${API}/class-schedule`, schedule);
        added++;
      } catch (error) {
        // Already exists, skip
        skipped++;
      }
    }
    
    toast.success(`Quick setup complete: ${added} added, ${skipped} already existed`);
    fetchData();
  };

  // Holiday Functions
  const handleAddHoliday = async () => {
    if (!newHoliday.start_date || !newHoliday.reason) {
      toast.error("Please enter date and reason");
      return;
    }
    
    try {
      await axios.post(`${API}/school-closures`, {
        ...newHoliday,
        end_date: newHoliday.end_date || newHoliday.start_date
      });
      toast.success("Holiday/Closure added");
      setHolidayDialogOpen(false);
      setNewHoliday({ start_date: "", end_date: "", reason: "", affects_class: "all" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add closure");
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!confirm("Delete this holiday/closure?")) return;
    try {
      await axios.delete(`${API}/school-closures/${id}`);
      toast.success("Closure removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete closure");
    }
  };

  // v7.4: Class Types Management Functions
  const handleAddClassType = async () => {
    if (!newClassName.trim()) { toast.error("Enter a class name"); return; }
    try {
      await axios.post(`${API}/class-types`, { name: newClassName.trim(), color: newClassColor });
      toast.success(`"${newClassName}" added`);
      setNewClassName("");
      setNewClassColor("gray");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add class type");
    }
  };

  const handleUpdateClassType = async () => {
    if (!editingClassType || !editClassName.trim()) return;
    try {
      await axios.put(`${API}/class-types/${editingClassType.id}`, { name: editClassName.trim(), color: editClassColor });
      toast.success("Class type updated");
      setEditingClassType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const handleDeleteClassType = async (ct) => {
    if (!confirm(`Delete "${ct.name}"? This won't delete student/payment data.`)) return;
    try {
      await axios.delete(`${API}/class-types/${ct.id}`);
      toast.success(`"${ct.name}" removed`);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete class type");
    }
  };

  // PIN Functions
  const handleChangePin = async () => {
    if (newPin.length !== 6) {
      toast.error("PIN must be 6 digits");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("PINs do not match");
      return;
    }
    
    setChangingPin(true);
    try {
      await axios.post(`${API}/auth/update-pin`, {
        old_pin: oldPin,
        new_pin: newPin
      });
      toast.success("PIN updated successfully");
      setOldPin("");
      setNewPin("");
      setConfirmPin("");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update PIN");
    } finally {
      setChangingPin(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  // Group schedule by class type — use dynamic classTypes from API
  const dynamicClassNames = classTypes.length > 0 ? classTypes.map(ct => ct.name) : CLASS_TYPES;
  const scheduleByClass = dynamicClassNames.map(classType => ({
    classType,
    days: classSchedule
      .filter(s => s.class_type === classType)
      .map(s => ({
        ...s,
        dayLabel: DAYS_OF_WEEK.find(d => d.value === s.day_of_week)?.label || s.day_of_week
      }))
  }));

  return (
    <div data-testid="settings-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-8 h-8 text-[#D4AF37]" />
        <div>
          <h1 
            className="text-2xl sm:text-3xl font-bold text-white uppercase tracking-wide"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Settings
          </h1>
          <p className="text-[#A1A1AA] text-sm">Manage class schedule, holidays, and app settings</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#18181b] border border-white/10 p-1">
          <TabsTrigger 
            value="schedule" 
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black uppercase tracking-wide text-xs"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Class Schedule
          </TabsTrigger>
          <TabsTrigger 
            value="holidays" 
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black uppercase tracking-wide text-xs"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <CalendarOff className="w-4 h-4 mr-2" />
            Holidays
          </TabsTrigger>
          <TabsTrigger 
            value="app" 
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black uppercase tracking-wide text-xs"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <Lock className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger 
            value="classes" 
            data-testid="manage-classes-tab"
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black uppercase tracking-wide text-xs"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Classes
          </TabsTrigger>
        </TabsList>

        {/* Class Schedule Tab */}
        <TabsContent value="schedule" className="space-y-6">
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle 
                    className="text-lg font-semibold text-white uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    Weekly Class Schedule
                  </CardTitle>
                  <p className="text-[#A1A1AA] text-sm mt-1">
                    Configure which days each class runs. This is used to calculate expected attendance.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleQuickSetup}
                    data-testid="quick-setup-btn"
                    className="bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold uppercase tracking-wider"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Quick Setup (Mon/Wed)
                  </Button>
                  <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="add-class-btn"
                      className="bg-[#D4AF37] hover:bg-[#b8972e] text-black font-semibold uppercase tracking-wider"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Class
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#18181b] border-white/10 text-white">
                    <DialogHeader>
                      <DialogTitle 
                        className="text-xl font-bold uppercase tracking-wide"
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        Add Class to Schedule
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA]">Class Type</Label>
                        <Select 
                          value={newSchedule.class_type} 
                          onValueChange={(v) => setNewSchedule({...newSchedule, class_type: v})}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue placeholder="Select class type" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27272a] border-white/10">
                            {dynamicClassNames.map(type => (
                              <SelectItem key={type} value={type} className="text-white hover:bg-white/10">
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA]">Day of Week</Label>
                        <Select 
                          value={newSchedule.day_of_week} 
                          onValueChange={(v) => setNewSchedule({...newSchedule, day_of_week: v})}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue placeholder="Select day" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27272a] border-white/10">
                            {DAYS_OF_WEEK.map(day => (
                              <SelectItem key={day.value} value={day.value} className="text-white hover:bg-white/10">
                                {day.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA]">Start Time (optional)</Label>
                        <Input
                          type="time"
                          value={newSchedule.start_time}
                          onChange={(e) => setNewSchedule({...newSchedule, start_time: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <Button
                        onClick={handleAddSchedule}
                        className="w-full bg-[#D4AF37] hover:bg-[#b8972e] text-black font-semibold uppercase"
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        Add to Schedule
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid gap-6 md:grid-cols-3">
                {scheduleByClass.map(({ classType, days }) => (
                  <div key={classType} className="bg-white/5 rounded-sm p-4">
                    <h3 
                      className="text-white font-semibold uppercase tracking-wide mb-3 pb-2 border-b border-white/10"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                    >
                      {classType.replace(" Kung Fu", "")}
                    </h3>
                    {days.length === 0 ? (
                      <p className="text-[#A1A1AA] text-sm italic">No classes scheduled</p>
                    ) : (
                      <div className="space-y-2">
                        {days.map((schedule) => (
                          <div 
                            key={schedule.id} 
                            className="flex items-center justify-between bg-white/5 rounded-sm px-3 py-2"
                          >
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-[#D4AF37]" />
                              <span className="text-white text-sm">{schedule.dayLabel}</span>
                              {schedule.start_time && (
                                <span className="text-[#A1A1AA] text-xs">@ {schedule.start_time}</span>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteSchedule(schedule.id)}
                              className="text-red-400 hover:bg-red-500/10 h-7 w-7 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[#A1A1AA] text-xs mt-3">
                      {days.length} class{days.length !== 1 ? 'es' : ''}/week ≈ {Math.round(days.length * 4.3)} classes/month
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Holidays Tab */}
        <TabsContent value="holidays" className="space-y-6">
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle 
                    className="text-lg font-semibold text-white uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    Holidays & Closures
                  </CardTitle>
                  <p className="text-[#A1A1AA] text-sm mt-1">
                    Mark dates when the school is closed. These days won't count against student attendance.
                  </p>
                </div>
                <Dialog open={holidayDialogOpen} onOpenChange={setHolidayDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="add-holiday-btn"
                      className="bg-[#D4AF37] hover:bg-[#b8972e] text-black font-semibold uppercase tracking-wider"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Closure
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#18181b] border-white/10 text-white">
                    <DialogHeader>
                      <DialogTitle 
                        className="text-xl font-bold uppercase tracking-wide"
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        Add Holiday/Closure
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">Start Date *</Label>
                          <Input
                            type="date"
                            value={newHoliday.start_date}
                            onChange={(e) => setNewHoliday({...newHoliday, start_date: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">End Date (for ranges)</Label>
                          <Input
                            type="date"
                            value={newHoliday.end_date}
                            onChange={(e) => setNewHoliday({...newHoliday, end_date: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA]">Reason *</Label>
                        <Input
                          value={newHoliday.reason}
                          onChange={(e) => setNewHoliday({...newHoliday, reason: e.target.value})}
                          placeholder="e.g., Bank Holiday, Christmas Break"
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA]">Affects</Label>
                        <Select 
                          value={newHoliday.affects_class} 
                          onValueChange={(v) => setNewHoliday({...newHoliday, affects_class: v})}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27272a] border-white/10">
                            <SelectItem value="all" className="text-white hover:bg-white/10">All Classes</SelectItem>
                            {dynamicClassNames.map(type => (
                              <SelectItem key={type} value={type} className="text-white hover:bg-white/10">
                                {type} Only
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleAddHoliday}
                        className="w-full bg-[#D4AF37] hover:bg-[#b8972e] text-black font-semibold uppercase"
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        Add Closure
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {holidays.length === 0 ? (
                <div className="p-6 text-center text-[#A1A1AA]">
                  <CalendarOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No holidays or closures added yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Date(s)</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Reason</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Affects</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {holidays.map((holiday) => (
                      <TableRow key={holiday.id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="font-medium text-white">
                          {formatDate(holiday.start_date)}
                          {holiday.end_date && holiday.end_date !== holiday.start_date && (
                            <span className="text-[#A1A1AA]"> → {formatDate(holiday.end_date)}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-white">{holiday.reason}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-sm text-xs ${
                            holiday.affects_class === 'all' 
                              ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                              : 'bg-white/10 text-white'
                          }`}>
                            {holiday.affects_class === 'all' ? 'All Classes' : holiday.affects_class.replace(" Kung Fu", "")}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteHoliday(holiday.id)}
                            className="text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* App Settings Tab */}
        <TabsContent value="app" className="space-y-6">
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <CardTitle 
                className="text-lg font-semibold text-white uppercase tracking-wide"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Change PIN
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="max-w-sm space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Current PIN</Label>
                  <Input
                    type="password"
                    maxLength={6}
                    value={oldPin}
                    onChange={(e) => setOldPin(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter current PIN"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">New PIN (6 digits)</Label>
                  <Input
                    type="password"
                    maxLength={6}
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter new PIN"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Confirm New PIN</Label>
                  <Input
                    type="password"
                    maxLength={6}
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                    placeholder="Confirm new PIN"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <Button
                  onClick={handleChangePin}
                  disabled={changingPin || !oldPin || !newPin || !confirmPin}
                  className="bg-[#D92323] hover:bg-[#b91c1c] text-white font-semibold uppercase"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {changingPin ? "Updating..." : "Update PIN"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* v7.4: Manage Classes Tab */}
        <TabsContent value="classes" className="space-y-6">
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <div>
                <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Manage Class Types
                </CardTitle>
                <p className="text-[#A1A1AA] text-sm mt-1">
                  Add, edit, or remove class types. Changes sync across Students, Attendance, and Payments.
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Add New Class */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  data-testid="new-class-name"
                  value={newClassName}
                  onChange={(e) => setNewClassName(e.target.value)}
                  placeholder="New class name (e.g. San Shou)"
                  className="bg-white/5 border-white/10 text-white flex-1"
                />
                <Select value={newClassColor} onValueChange={setNewClassColor}>
                  <SelectTrigger className="w-full sm:w-32 bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10">
                    {["orange", "blue", "red", "teal", "green", "purple", "yellow", "gray"].map(c => (
                      <SelectItem key={c} value={c} className="text-white hover:bg-white/10 capitalize">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="add-class-type-btn"
                  onClick={handleAddClassType}
                  className="bg-[#D4AF37] hover:bg-[#b8972e] text-black font-semibold uppercase tracking-wider"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Class
                </Button>
              </div>

              {/* Class Types List */}
              <div className="space-y-2">
                {classTypes.length === 0 ? (
                  <p className="text-[#A1A1AA] text-center py-6">No class types configured. Add one above.</p>
                ) : classTypes.map((ct) => (
                  <div key={ct.id} className="flex items-center justify-between bg-white/5 rounded-sm px-4 py-3">
                    {editingClassType?.id === ct.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          value={editClassName}
                          onChange={(e) => setEditClassName(e.target.value)}
                          className="bg-white/10 border-white/10 text-white h-8 text-sm flex-1"
                        />
                        <Select value={editClassColor} onValueChange={setEditClassColor}>
                          <SelectTrigger className="w-24 bg-white/10 border-white/10 text-white h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27272a] border-white/10">
                            {["orange", "blue", "red", "teal", "green", "purple", "yellow", "gray"].map(c => (
                              <SelectItem key={c} value={c} className="text-white hover:bg-white/10 capitalize">{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" onClick={handleUpdateClassType} className="bg-[#22c55e] hover:bg-[#16a34a] text-white h-8">
                          <Save className="w-3 h-3 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingClassType(null)} className="text-[#A1A1AA] h-8">
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <span className={`w-3 h-3 rounded-full bg-${ct.color}-500`} style={{ backgroundColor: `var(--${ct.color}, ${ct.color})` }} />
                          <span className="text-white font-medium">{ct.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingClassType(ct); setEditClassName(ct.name); setEditClassColor(ct.color); }}
                            className="text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClassType(ct)}
                            className="text-red-400 hover:bg-red-500/10 h-8"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
